import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";

const SOLUTIONS = [
  {
    id: "packaging",
    title: "Packaging Solutions",
    color: "#E91E8C",
    animation: "https://lottie.host/0e913ecf-112d-4b4a-8381-f3744be6614a/Gi9SM9DkCt.lottie",
    description: "Business packaging solutions for food service, retail, distribution, and commercial operations.",
    features: [
      "Business Packaging Solutions",
      "Custom Supply Programs",
      "Industry Focused Support",
      "Reliable Distribution",
      "Operational Efficiency",
      "Quality Assurance",
    ],
    primaryBtn: { label: "Explore Solutions", action: "solutions" },
    secondaryBtn: {
      label: "Request Consultation",
      href: "https://wa.me/919821991932?text=Hi, I want to discuss packaging solutions",
    },
  },
  {
    id: "printing",
    title: "Printing Solutions",
    color: "#FF6B2B",
    animation: "https://lottie.host/5ddcd871-cc32-4889-a286-f39e30014743/QI4IYKBElQ.lottie",
    description: "Corporate branding, apparel printing, and promotional material solutions.",
    features: [
      "Corporate Branding & Merchandise",
      "Custom Apparel Printing",
      "Promotional Materials",
      "Bulk Production",
      "Brand Customization",
      "Quality Assured Delivery",
    ],
    secondaryBtn: {
      label: "Request Consultation",
      href: "https://wa.me/919821991932?text=Hi, I am interested in Printing Solutions",
    },
  },
  {
    id: "industrial",
    title: "Industrial Solutions",
    color: "#6B21A8",
    animation: "https://lottie.host/d28554bc-3f9e-4469-b53e-375ad173a71c/8cynbOXrrJ.lottie",
    description: "Future expansion into industrial products, manufacturing support, and business sourcing.",
    features: [
      "Industrial Products",
      "Manufacturing Support",
      "Bulk Sourcing Solutions",
      "Custom Manufacturing",
      "Supply Chain Integration",
      "Business Growth Partnership",
    ],
    secondaryBtn: {
      label: "Learn More",
      href: "https://wa.me/919821991932?text=Hi, tell me more about Industrial Solutions",
    },
  },
];

export default function Solutions() {
  const [activeTab, setActiveTab] = useState(0);
  const current = SOLUTIONS[activeTab];

  return (
    <section
      id="solutions"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: "clamp(40px,6vh,80px) 0",
        background: "#FFFFFF",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* bg glow */}
      <div style={{
        position: "absolute", width: 500, height: 500,
        right: -200, top: -150, borderRadius: "50%",
        background: "radial-gradient(circle,rgba(233,30,140,.08),transparent 70%)",
        filter: "blur(60px)", pointerEvents: "none",
      }} />

      <div className="container" style={{ width: "100%", padding: "0 20px" }}>

        {/* ── Section header ── */}
        <div style={{ textAlign: "center", marginBottom: "clamp(24px,4vh,48px)" }}>
          <span style={{
            display: "inline-block", padding: "6px 16px",
            borderRadius: 999, background: "rgba(233,30,140,.08)",
            color: "#E91E8C", fontWeight: 700, fontSize: ".75rem", letterSpacing: "1px",
          }}>
            OUR SOLUTIONS
          </span>

          <h2 style={{
            marginTop: "clamp(8px,1.5vh,16px)",
            fontSize: "clamp(1.6rem,3.2vw,2.8rem)",
            fontWeight: 800, lineHeight: 1.1,
          }}>
            Enterprise Solutions
            <br />Across Industries
          </h2>

          <p style={{
            maxWidth: 700, margin: "clamp(8px,1.5vh,16px) auto 0",
            color: "#666", lineHeight: 1.6, fontSize: "0.95rem",
          }}>
            Delivering packaging, procurement, distribution, and business support
            solutions that help organizations operate efficiently and scale with confidence.
          </p>
        </div>

        {/* ── Tab pills ── */}
        <div style={{
          display: "flex", gap: 10, flexWrap: "wrap",
          justifyContent: "center",
          marginBottom: "clamp(16px,3vh,28px)",
        }}>
          {SOLUTIONS.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(index)}
              style={{
                border: "none", cursor: "pointer",
                padding: "10px 20px", borderRadius: 999,
                fontWeight: 700, fontSize: "0.88rem",
                transition: "all .3s",
                background: activeTab === index
                  ? `linear-gradient(135deg,${item.color},${item.color}CC)`
                  : "#fff",
                color: activeTab === index ? "#fff" : "#333",
                boxShadow: activeTab === index
                  ? "0 10px 30px rgba(0,0,0,.12)"
                  : "0 4px 12px rgba(0,0,0,.05)",
              }}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* ── Main grid ── */}
        <div className="sol-grid" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "clamp(20px,3.5vw,50px)",
          alignItems: "center",
        }}>

          {/* Animation panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id + "-anim"}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.4 }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                minHeight: "clamp(220px,45vh,480px)",
              }}
            >
              <DotLottieReact
                src={current.animation}
                loop autoplay
                style={{ width: "100%", height: "100%", maxWidth: 480, maxHeight: 480 }}
              />
            </motion.div>
          </AnimatePresence>

          {/* Content panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.35 }}
              style={{
                background: "rgba(255,255,255,.75)",
                backdropFilter: "blur(16px)",
                borderRadius: 30,
                padding: "clamp(20px,3vw,36px)",
                boxShadow: "0 25px 60px rgba(0,0,0,.08)",
              }}
            >
              <h3 style={{
                fontSize: "clamp(1.2rem,2.2vw,1.7rem)",
                fontWeight: 800, marginTop: 0, marginBottom: 8,
              }}>
                {current.title}
              </h3>

              <p style={{ color: "#666", lineHeight: 1.55, fontSize: "0.92rem", marginTop: 0 }}>
                {current.description}
              </p>

              <div style={{
                marginTop: "clamp(12px,2vh,20px)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px 14px",
              }}>
                {current.features.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}>
                    <FiCheckCircle color={current.color} size={14} style={{ flexShrink: 0 }} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <div style={{
                display: "flex", gap: 10, flexWrap: "wrap",
                marginTop: "clamp(16px,2.5vh,26px)",
              }}>
                {current.primaryBtn && (
                  <button
                    onClick={() =>
                      document.getElementById(current.primaryBtn.action)?.scrollIntoView({ behavior: "smooth" })
                    }
                    style={{
                      border: "none", cursor: "pointer",
                      padding: "11px 20px", borderRadius: 14,
                      background: current.color, color: "#fff",
                      fontWeight: 700, fontSize: "0.88rem",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <FiArrowRight /> {current.primaryBtn.label}
                  </button>
                )}
                <a
                  href={current.secondaryBtn.href}
                  target="_blank" rel="noreferrer"
                  style={{
                    textDecoration: "none",
                    padding: "11px 20px", borderRadius: 14,
                    border: `2px solid ${current.color}`,
                    color: current.color, fontWeight: 700, fontSize: "0.88rem",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <FaWhatsapp /> {current.secondaryBtn.label}
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .sol-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .sol-grid > *:first-child {
            min-height: 220px !important;
            max-height: 280px;
          }
        }

        @media (max-width: 480px) {
          .sol-grid > *:first-child {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
}